import React, { useContext, useMemo, useState } from 'react';
import { todaysDate } from '../../../utils/DateHelper';
import { useValueState } from '../../../utils/hooks';
import { useSalesOrderConfig } from '../../../utils/FetchConfig';
import { useDispatch } from 'react-redux';
import { BasicHeader } from '../../../actions/BasicHeader';
import {
  AlertType,
  IAutomaticGenerateDispatchInstructionsForConsolidatedPicksMutationResult,
  IAutomaticGenerateDispatchInstructionsForConsolidatedPicksMutationVariables,
  IAutomaticGenerateDispatchInstructionsForImportedAmazonOrdersMutationResult,
  IAutomaticGenerateDispatchInstructionsForImportedAmazonOrdersMutationVariables,
  IAutomaticGenerateDispatchInstructionsForImportedEbayOrdersMutationResult,
  IAutomaticGenerateDispatchInstructionsForImportedEbayOrdersMutationVariables,
  IAutomaticGenerateDispatchInstructionsForImportedEtsyOrdersMutationResult,
  IAutomaticGenerateDispatchInstructionsForImportedEtsyOrdersMutationVariables,
  IAutomaticGenerateDispatchInstructionsForImportedShopifyOrdersMutationResult,
  IAutomaticGenerateDispatchInstructionsForImportedShopifyOrdersMutationVariables,
  IAutomaticGenerateDispatchInstructionsForImportedWebOrdersMutationResult,
  IAutomaticGenerateDispatchInstructionsForImportedWebOrdersMutationVariables,
  IAutomaticGenerateDispatchInstructionsForOrderLinesMutationResult,
  IAutomaticGenerateDispatchInstructionsForOrderLinesMutationVariables,
  IAutomaticGenerateDispatchInstructionsMutationResult,
  IAutomaticGenerateDispatchInstructionsMutationVariables,
  IConsolidatedPick,
  IDeliveryMethod,
  IPickingInstruction,
  StockAdjustmentForInsufficientStock,
} from '../../../graphql/schema/p4types';
import { ModalBottomButtonContainer } from '../../modals/Styles';
import { DateField } from '../../connectedForm/inputComponents';
import CheckboxInput from '../../connectedForm/CheckboxInput/CheckboxInput';
import { Button, SuccessButton } from '../../connectedForm/Button';
import { Link, useHistory } from 'react-router-dom';
import ManualDispatch from '../../../graphql/entities/sales/ManualDispatch';
import DeliveryMethods from '../../../graphql/entities/sales/DeliveryMethods';
import { IJobMeta, useBackgroundMutation } from '../../../utils/BackgroundJobs';
import AutomaticGenerateDispatchInstructions from '../../../graphql/mutations/sales/AutomaticGenerateDispatchInstructions.graphql';
import AutomaticGenerateImportedAmazonOrders from '../../../graphql/mutations/sales/AutomaticGenerateImportedAmazonOrders.graphql';
import AutomaticGenerateImportedEbayOrders from '../../../graphql/mutations/sales/AutomaticGenerateImportedEbayOrders.graphql';
import AutomaticGenerateImportedEtsyOrders from '../../../graphql/mutations/sales/AutomaticGenerateImportedEtsyOrders.graphql';
import AutomaticGenerateImportedShopifyOrders from '../../../graphql/mutations/sales/AutomaticGenerateImportedShopifyOrders.graphql';
import AutomaticGenerateImportedWebOrders from '../../../graphql/mutations/sales/AutomaticGenerateImportedWebOrders.graphql';
import { DispatchResultNotification, useDispatchResultToast } from './DispatchResultNotification';
import AutomaticGenerateDispatchInstructionsForOrderLines from '../../../graphql/mutations/sales/AutomaticGenerateDispatchInstructionsForOrderLines.graphql';
import AutomaticGenerateDispatchInstructionsForConsolidatedPicks from '../../../graphql/mutations/sales/AutomaticGenerateDispatchInstructionsForConsolidatedPicks.graphql';
import getClientMutationId from '../../../utils/getClientMutationId';
import normalizeVariables from '../../../graphql/schema/normalizeVariables';
import { serviceActions } from '../../../state/modules/service';
import ConnectedFormContext from '../../Context/ConnectedFormContext';
import WorkspaceContext from '../../Context/WorkspaceContext';

import { useActionCriteria } from '../../../utils/useActionProps';
import { DocumentNode } from '@apollo/client';
import { InfoRegular } from '@fluentui/react-icons';
import QueryDropdown from '../../connectedForm/QueryDropdown';
import { useP4ToastController } from '../../../views/system/NotificationsArea';
import FluentModal from '../../modals/FluentModal';
import { Maybe } from 'graphql/jsutils/Maybe';

type IImportedOrderResult =
  | IAutomaticGenerateDispatchInstructionsForImportedAmazonOrdersMutationResult
  | IAutomaticGenerateDispatchInstructionsForImportedEbayOrdersMutationResult
  | IAutomaticGenerateDispatchInstructionsForImportedEtsyOrdersMutationResult
  | IAutomaticGenerateDispatchInstructionsForImportedShopifyOrdersMutationResult
  | IAutomaticGenerateDispatchInstructionsForImportedWebOrdersMutationResult;

type IImportedOrderVariables =
  | IAutomaticGenerateDispatchInstructionsForImportedAmazonOrdersMutationVariables
  | IAutomaticGenerateDispatchInstructionsForImportedEbayOrdersMutationVariables
  | IAutomaticGenerateDispatchInstructionsForImportedEtsyOrdersMutationVariables
  | IAutomaticGenerateDispatchInstructionsForImportedShopifyOrdersMutationVariables
  | IAutomaticGenerateDispatchInstructionsForImportedWebOrdersMutationVariables;

const useImportedOrderMutation = (
  source: DispatchSource,
  onCompleteDispatchJob: (job: IJobMeta) => void,
  onFailedDispatchJob: (job: IJobMeta) => void
) => {
  let mutation: undefined | DocumentNode;
  switch (source) {
    case DispatchSource.ImportedAmazonOrderWorkspace:
      mutation = AutomaticGenerateImportedAmazonOrders;
      break;
    case DispatchSource.ImportedEbayOrderWorkspace:
      mutation = AutomaticGenerateImportedEbayOrders;
      break;
    case DispatchSource.ImportedEtsyOrderWorkspace:
      mutation = AutomaticGenerateImportedEtsyOrders;
      break;
    case DispatchSource.ImportedShopifyOrderWorkspace:
      mutation = AutomaticGenerateImportedShopifyOrders;
      break;
    case DispatchSource.ImportedWebOrderWorkspace:
      mutation = AutomaticGenerateImportedWebOrders;
      break;
    default:
      console.error('A valid dispatch source must be provided for this hook.');
      break;
  }

  const { dispatchSimpleToast } = useP4ToastController();
  return useBackgroundMutation<IImportedOrderResult, IImportedOrderVariables>(mutation!, {
    jobTitle: 'Dispatch Imported Sales Orders',
    onCompleteJob: onCompleteDispatchJob,
    onFailedJob: onFailedDispatchJob,
    onError: () => {
      dispatchSimpleToast('Dispatch Orders', 'Orders could not be dispatched', AlertType.Warning);
    },
  });
};

const useAutomaticDispatch = (
  {
    dispatchDate,
    targetDate,
    reconcile,
    source,
    deliveryMethod,
  }: {
    dispatchDate: unknown;
    targetDate: unknown;
    reconcile: boolean;
    source: DispatchSource;
    deliveryMethod: IDeliveryMethod | null;
  },
  { onClose }: { onClose: Function }
) => {
  const dispatch = useDispatch();
  const { reloadData: formReloadData, values } = useContext(ConnectedFormContext);
  const { reloadData: workspaceReloadData } = useContext(WorkspaceContext);

  const dispatchResultToast = useDispatchResultToast();

  const onCompleteDispatchJob = (job: IJobMeta) => {
    //Since we have multiple access points to get to here, check/call both form/workspace reloads (Expect only one to actually trigger).
    formReloadData?.(true);
    workspaceReloadData?.();
    dispatchResultToast(job);
  };

  const onFailedDispatchJob = (job?: IJobMeta) => {
    //If the job fails, we still want a result toast so we do a similar behaviour to 'complete' - Just the results will all be failure details.
    if (job) {
      dispatchResultToast(job);
    }
  };

  const [autoDispatchOrdersMutation] = useBackgroundMutation<
    IAutomaticGenerateDispatchInstructionsMutationResult,
    IAutomaticGenerateDispatchInstructionsMutationVariables
  >(AutomaticGenerateDispatchInstructions, {
    jobTitle: 'Dispatch Sales Orders',
    onCompleteJob: onCompleteDispatchJob,
    onFailedJob: onFailedDispatchJob,
    onError: () => {
      dispatch(serviceActions.genericAlert('Dispatch Orders', 'Orders could not be dispatched', AlertType.Warning));
    },
  });
  const [autoDispatchOrderLinesMutation] = useBackgroundMutation<
    IAutomaticGenerateDispatchInstructionsForOrderLinesMutationResult,
    IAutomaticGenerateDispatchInstructionsForOrderLinesMutationVariables
  >(AutomaticGenerateDispatchInstructionsForOrderLines, {
    jobTitle: 'Dispatch Sales Order Lines',
    onCompleteJob: onCompleteDispatchJob,
    onFailedJob: onFailedDispatchJob,
    onError: () => {
      dispatch(
        serviceActions.genericAlert('Dispatch Order Lines', 'Order lines could not be dispatched', AlertType.Warning)
      );
    },
  });
  const [autoDispatchConsolidatedPicksMutation] = useBackgroundMutation<
    IAutomaticGenerateDispatchInstructionsForConsolidatedPicksMutationResult,
    IAutomaticGenerateDispatchInstructionsForConsolidatedPicksMutationVariables
  >(AutomaticGenerateDispatchInstructionsForConsolidatedPicks, {
    jobTitle: 'Dispatch Consolidated Picks',
    onCompleteJob: onCompleteDispatchJob,
    onFailedJob: onFailedDispatchJob,
    onError: () => {
      dispatch(
        serviceActions.genericAlert(
          'Dispatch Consolidated Picks',
          'Consolidated picks could not be dispatched',
          AlertType.Warning
        )
      );
    },
  });
  const [autoDispatchAmazonOrdersMutation] = useImportedOrderMutation(
    DispatchSource.ImportedAmazonOrderWorkspace,
    onCompleteDispatchJob,
    onFailedDispatchJob
  );
  const [autoDispatchEbayOrdersMutation] = useImportedOrderMutation(
    DispatchSource.ImportedEbayOrderWorkspace,
    onCompleteDispatchJob,
    onFailedDispatchJob
  );
  const [autoDispatchEtsyOrdersMutation] = useImportedOrderMutation(
    DispatchSource.ImportedEtsyOrderWorkspace,
    onCompleteDispatchJob,
    onFailedDispatchJob
  );
  const [autoDispatchShopifyOrdersMutation] = useImportedOrderMutation(
    DispatchSource.ImportedShopifyOrderWorkspace,
    onCompleteDispatchJob,
    onFailedDispatchJob
  );
  const [autoDispatchWebOrdersMutation] = useImportedOrderMutation(
    DispatchSource.ImportedWebOrderWorkspace,
    onCompleteDispatchJob,
    onFailedDispatchJob
  );

  const actionCriteria = useActionCriteria();

  const dispatchCriteria = {
    dispatchDate,
    targetDate,
    reconcile,
    deliveryMethod,
  };

  return () => {
    if (dispatchDate !== '') {
      const variables = {
        clientMutationId: getClientMutationId(),
        dispatchCriteria,
      };

      switch (source) {
        case DispatchSource.SalesOrderWorkspace:
          autoDispatchOrdersMutation({
            variables: { input: normalizeVariables({ ...variables, actionCriteria }) },
          });
          break;
        case DispatchSource.SalesOrderLineWorkspace:
          autoDispatchOrderLinesMutation({
            variables: { input: normalizeVariables({ ...variables, actionCriteria }) },
          });
          break;
        case DispatchSource.ImportedAmazonOrderWorkspace:
          autoDispatchAmazonOrdersMutation({
            variables: { input: normalizeVariables({ ...variables, actionCriteria }) },
          });
          break;
        case DispatchSource.ImportedEbayOrderWorkspace:
          autoDispatchEbayOrdersMutation({
            variables: { input: normalizeVariables({ ...variables, actionCriteria }) },
          });
          break;
        case DispatchSource.ImportedEtsyOrderWorkspace:
          autoDispatchEtsyOrdersMutation({
            variables: { input: normalizeVariables({ ...variables, actionCriteria }) },
          });
          break;
        case DispatchSource.ImportedShopifyOrderWorkspace:
          autoDispatchShopifyOrdersMutation({
            variables: { input: normalizeVariables({ ...variables, actionCriteria }) },
          });
          break;
        case DispatchSource.ImportedWebOrderWorkspace:
          autoDispatchWebOrdersMutation({
            variables: { input: normalizeVariables({ ...variables, actionCriteria }) },
          });
          break;
        case DispatchSource.SalesOrderForm:
          autoDispatchOrdersMutation({
            variables: {
              input: normalizeVariables({
                ...variables,
                actionCriteria: {
                  actionEntities: values?.id ? [values] : [],
                  include: true,
                  filters: [],
                },
              }),
            },
          });
          break;
        case DispatchSource.ConsolidatedPickWorkspace:
          autoDispatchConsolidatedPicksMutation({
            variables: {
              input: normalizeVariables({
                ...variables,
                actionCriteria: normalizeVariables(actionCriteria, [
                  'uniqueCarriers',
                  'salesOrderCount',
                  'itemCount',
                  'pickedItemCount',
                  'toPickItemCount',
                ]),
              }),
            },
          });
          break;
        default:
          console.error('Unsupported dispatch source');
      }

      onClose();
    }
  };
};

export enum DispatchSource {
  SalesOrderForm = 'SALES_ORDER_FORM',
  SalesOrderWorkspace = 'SALES_ORDER_WORKSPACE',
  SalesOrderLineWorkspace = 'SALES_ORDER_LINE_WORKSPACE',
  ImportedAmazonOrderWorkspace = 'IMPORTED_AMAZON_ORDER_WORKSPACE',
  ImportedEbayOrderWorkspace = 'IMPORTED_EBAY_ORDER_WORKSPACE',
  ImportedEtsyOrderWorkspace = 'IMPORTED_ETSY_ORDER_WORKSPACE',
  ImportedShopifyOrderWorkspace = 'IMPORTED_SHOPIFY_ORDER_WORKSPACE',
  ImportedWebOrderWorkspace = 'IMPORTED_WEB_ORDER_WORKSPACE',
  ConsolidatedPickWorkspace = 'CONSOLIDATED_PICK_WORKSPACE',
}

/**
 * Modal for selecting date for dispatch + manual or auto dispatch type.
 * @param isOpen        - Is dispatch modal open (manual/auto selection).
 * @param source        - Whether this is the form or workspace triggering the action. [FORM, WORKSPACE]
 * @param onClose       - Function to trigger when the dispatch modal is closed.
 */
export const DispatchModal = ({
  isOpen,
  onClose,
  source,
}: {
  isOpen: boolean;
  source: DispatchSource;
  onClose: () => void;
}) => {
  const [dispatchDate, setDispatchDate] = useState(todaysDate());
  const [targetDate, setTargetDate] = useState(null);
  const [reconcile, setReconcile] = useValueState(false);
  const [deliveryMethod, setDeliveryMethod] = useState(null);

  const orderConfig = useSalesOrderConfig();
  const history = useHistory();

  const auto = useAutomaticDispatch({ dispatchDate, targetDate, reconcile, source, deliveryMethod }, { onClose });
  const connectedForm = useContext(ConnectedFormContext);
  const workspaceContext = useContext(WorkspaceContext);

  const manualSalesOrders = useMemo(() => {
    if (source === DispatchSource.SalesOrderForm) {
      return [connectedForm.values];
    }
    const selectionState = workspaceContext.workspaceSelection?.getSelectionState();
    if (!selectionState) {
      return [];
    }
    if (selectionState.selectedAll) {
      //Negative/All selection for "Manual" is not currently supported - We would either need to:
      // - Alter ManualDispatchForm to handle ActionCriteria of each type (SalesOrder, SalesOrderLine, ImportedAmazonOrder, ImportedEbayOrder, etc) with appropriate paging support.
      // - Query for all the salesOrderId's here, for us to pass through to Manual Dispatch.
      return [];
    }
    switch (source) {
      case DispatchSource.SalesOrderWorkspace:
        return selectionState.actionEntities;
      case DispatchSource.SalesOrderLineWorkspace:
        const salesOrders = selectionState.actionEntities.map((value) => value.salesOrder);
        //De-duplicate orders; We don't want to cause/risk the same order to be processed multiple times.
        return salesOrders.filter((order, index, self) => index === self.findIndex((o) => o.id === order.id));
      case DispatchSource.ImportedAmazonOrderWorkspace:
      case DispatchSource.ImportedEbayOrderWorkspace:
      case DispatchSource.ImportedEtsyOrderWorkspace:
      case DispatchSource.ImportedShopifyOrderWorkspace:
      case DispatchSource.ImportedWebOrderWorkspace:
        return selectionState.actionEntities.map((importedOrder) => importedOrder.salesOrder);
      case DispatchSource.ConsolidatedPickWorkspace:
        return selectionState.actionEntities
          .flatMap((consolidatedPick: IConsolidatedPick) => consolidatedPick?.pickingInstructions)
          .map((pickingInstruction: Maybe<IPickingInstruction>) => pickingInstruction?.salesOrder!)
          .filter((order, index, self) => index === self.findIndex((o) => o.id === order.id));
      default:
        return [];
    }
  }, [connectedForm.values, source, workspaceContext.workspaceSelection]);

  return (
    <FluentModal isOpen={isOpen} styles={{ main: { minWidth: '500px', paddingBottom: '60px' } }}>
      <BasicHeader>Dispatch</BasicHeader>
      <div style={{ padding: '1em' }}>
        {manualSalesOrders.length === 0 ? (
          <p style={{ paddingBottom: '3px' }}>
            <InfoRegular style={{ verticalAlign: 'sub', marginRight: '5px', height: '17px' }} />
            Manual dispatch is only available for positive selections (Check-all is not supported)
          </p>
        ) : null}
        <DateField
          label="Date Required"
          value={targetDate}
          onChange={setTargetDate}
          hint="Orders/order lines with an expected delivery date after this date will not be included"
          labelCols={5 / 12}
          inputCols={7 / 12}
          data-testid={'date-required'}
        />
        <DateField
          label="Dispatch Date"
          value={dispatchDate}
          onChange={setDispatchDate}
          labelCols={5 / 12}
          inputCols={7 / 12}
          data-testid={'dispatch-date'}
        />
        {orderConfig?.dispatchFromFreeStock &&
        orderConfig?.stockAdjustmentForInsufficientStock === StockAdjustmentForInsufficientStock.Automatic ? (
          <CheckboxInput
            label="Reconcile adjustments"
            hint="Automatic stock adjustments for insufficient stock are enabled, select this option to flag the movements for reconciliation on future receipts"
            value={reconcile}
            onChange={setReconcile}
            labelCols={5 / 12}
            inputCols={7 / 12}
            data-testid={'reconcile-adjustments'}
          />
        ) : null}
        <QueryDropdown
          label={'Override Delivery Method'}
          {...DeliveryMethods}
          onChange={setDeliveryMethod}
          value={deliveryMethod}
          renderInBody={false}
          labelCols={5 / 12}
          inputCols={7 / 12}
          data-testid={'set-delivery-method'}
        />
        <ModalBottomButtonContainer>
          <Button data-testid="dispatchModal-cancel" onClick={onClose}>
            Cancel
          </Button>
          {manualSalesOrders.length > 0 ? (
            <Link
              to={{
                pathname: ManualDispatch.formUrl,
                state: {
                  dispatchDate,
                  targetDate,
                  deliveryMethod,
                  salesOrders: manualSalesOrders,
                  reconcileAdjustments: reconcile,
                  previousLocation: history.location?.pathname || 'back',
                },
              }}
            >
              <SuccessButton data-testid="dispatchModal-allocate">Dispatch</SuccessButton>
            </Link>
          ) : null}
          <SuccessButton
            data-testid="dispatchModal-auto"
            onClick={auto}
            autoFocus={
              true /*This is required to prevent the datepicker from auto-focusing when the modal opens, which is not desired behaviour*/
            }
          >
            Auto
          </SuccessButton>
        </ModalBottomButtonContainer>
      </div>
    </FluentModal>
  );
};
